import { styled } from '@linaria/react';
import { Loader } from '@mantine/core';
import { useCallback, useState } from 'react';
import { useDeleteFloraMemory, useFloraMemories } from '../../../../api/agents-client/agent-client.hooks';
import type { FloraMemory } from '../../../../api/agents-client/agent-client.type';
import { Icon } from '../../../../ui-library/icon/icon';
import { Text } from '../../../../ui-library/typography/typography';
import { DeleteConfirmationModal } from '../../../data-management/components/delete-confirmation-modal';
import { AgentViewHeader } from '../agent-view-header';

// Number of lines a long memory is clamped to before "Show more", and the
// content length above which a memory is treated as long. Both the clamp and
// the toggle are gated on the same threshold, so a memory is never visually
// truncated without an accompanying way to expand it.
const MEMORY_PREVIEW_LINES = 4;
const MEMORY_LONG_THRESHOLD = 320;

/**
 * Maps a memory's `source` to a short, human-readable author label.
 *
 * @param {FloraMemory['source']} source - Who created the memory.
 * @returns {string} "Flora" for agent-inferred memories, "You" otherwise.
 */
function sourceLabel(source: FloraMemory['source']): string {
  return source === 'agent_inferred' ? 'Flora' : 'You';
}

interface MemoryItemProps {
  memory: FloraMemory;
  onRequestDelete: (memory: FloraMemory) => void;
}

/**
 * A single memory card. Long content is clamped to a few lines behind a
 * "Show more"/"Show less" toggle; short content renders in full with no toggle.
 * Each card owns its own expanded state so toggling one leaves the others
 * untouched.
 *
 * @param {MemoryItemProps} props - The memory to render and the delete request handler.
 * @returns {JSX.Element} The memory card.
 */
function MemoryItem({ memory, onRequestDelete }: MemoryItemProps) {
  const [expanded, setExpanded] = useState(false);
  const isLong = memory.content.length > MEMORY_LONG_THRESHOLD;
  const isClamped = isLong && !expanded;

  return (
    <MemoryCard>
      <MemoryHeader>
        <Text size="small" weight="bold" color="var(--ink-darkest)">
          {memory.title}
        </Text>
        <DeleteButton
          type="button"
          aria-label={`Delete memory ${memory.title}`}
          onClick={() => onRequestDelete(memory)}
        >
          <Icon name="delete" size={16} color="var(--ink-lighter)" style={{ cursor: 'pointer' }} />
        </DeleteButton>
      </MemoryHeader>
      <MemoryContent $clamped={isClamped} $lines={MEMORY_PREVIEW_LINES}>
        {memory.content}
      </MemoryContent>
      {isLong && (
        <ExpandToggle type="button" aria-expanded={expanded} onClick={() => setExpanded((value) => !value)}>
          {expanded ? 'Show less' : 'Show more'}
        </ExpandToggle>
      )}
      <MemoryMeta>
        <TypeChip>{memory.memory_type}</TypeChip>
        <Text size="tiny" color="var(--ink-lighter)">
          Saved by {sourceLabel(memory.source)}
        </Text>
      </MemoryMeta>
    </MemoryCard>
  );
}

interface AgentMemoriesViewProps {
  /** Render inside the settings layout: compact heading, no page padding or scrolling. */
  embedded?: boolean;
}

/**
 * Catalog of long-term memories Flora has saved for the user (org-scoped).
 * Lists every memory regardless of source and lets the user delete any of them
 * via the shared type-the-title `DeleteConfirmationModal`. A single modal
 * instance is reused; the row that triggered it is held in `selected`.
 *
 * @param {AgentMemoriesViewProps} props - Whether the view renders inside the settings layout
 * @returns {JSX.Element} The "Flora's Memories" section.
 */
export function AgentMemoriesView({ embedded = false }: AgentMemoriesViewProps = {}) {
  const { memories, isLoading, error } = useFloraMemories();
  const deleteMemory = useDeleteFloraMemory();

  const [selected, setSelected] = useState<FloraMemory | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenDelete = useCallback((memory: FloraMemory) => {
    setSelected(memory);
    setIsModalOpen(true);
  }, []);

  const handleCloseDelete = useCallback(() => {
    setIsModalOpen(false);
    setSelected(null);
    deleteMemory.reset();
  }, [deleteMemory]);

  if (isLoading) {
    return (
      <Container data-embedded={embedded || undefined}>
        <CenterWrap>
          <AgentViewHeader
            title={embedded ? 'Memories' : "Flora's Memories"}
            variant={embedded ? 'settings-section' : 'page'}
          />
          <Content>
            <LoadingState>
              <Loader size="sm" color="var(--secondary-base)" />
            </LoadingState>
          </Content>
        </CenterWrap>
      </Container>
    );
  }

  if (error) {
    return (
      <Container data-embedded={embedded || undefined}>
        <CenterWrap>
          <AgentViewHeader
            title={embedded ? 'Memories' : "Flora's Memories"}
            variant={embedded ? 'settings-section' : 'page'}
          />
          <Content>
            <EmptyState>
              <Text size="regular" color="var(--ink-lighter)">
                Failed to load memories.
              </Text>
            </EmptyState>
          </Content>
        </CenterWrap>
      </Container>
    );
  }

  return (
    <Container data-embedded={embedded || undefined}>
      <CenterWrap>
        <AgentViewHeader
          title={embedded ? 'Memories' : "Flora's Memories"}
          variant={embedded ? 'settings-section' : 'page'}
          subtitle={`${memories.length} ${memories.length === 1 ? 'memory' : 'memories'}`}
        />
        <Content>
          <FormContent>
            <Text size="small" color="var(--ink-lighter)" style={{ marginBottom: 16 }}>
              These are long-term memories Flora has saved to recall in future conversations. Delete any you no longer
              want Flora to remember.
            </Text>

            {memories.length === 0 ? (
              <EmptyState>
                <Icon name="psychology" color="var(--ink-lighter)" size={40} />
                <Text size="regular" color="var(--ink-lighter)" style={{ marginTop: 12 }}>
                  Flora hasn't saved any memories yet.
                </Text>
              </EmptyState>
            ) : (
              <MemoryList>
                {memories.map((memory) => (
                  <MemoryItem key={memory.id} memory={memory} onRequestDelete={handleOpenDelete} />
                ))}
              </MemoryList>
            )}
          </FormContent>
        </Content>
      </CenterWrap>

      <DeleteConfirmationModal
        isOpen={isModalOpen && selected !== null}
        onClose={handleCloseDelete}
        onDelete={() => (selected ? deleteMemory.mutateAsync(selected.id) : undefined)}
        title="Delete Memory"
        itemName={selected?.title ?? ''}
        isDeleting={deleteMemory.isPending}
        error={deleteMemory.error}
      />
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  padding: 40px 0;
  overflow-y: auto;

  &[data-embedded] {
    height: auto;
    padding: 0;
    overflow: visible;
  }
`;

const CenterWrap = styled.div`
  width: 100%;
`;

const Content = styled.div`
  flex: 1;
`;

const FormContent = styled.div`
  width: 100%;
  max-width: 720px;
  margin: 0 auto;

  [data-embedded] & {
    max-width: none;
    margin: 0;
    padding-bottom: 0;
  }
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 4px;
  min-height: 200px;
`;

const MemoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MemoryCard = styled.div`
  display: flex;
  flex-direction: column;
  padding: 14px 16px;
  border-radius: 12px;
  background: var(--sky-lightest);
  border: 1px solid var(--sky-light);
`;

const MemoryHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const MemoryContent = styled.div<{ $clamped: boolean; $lines: number }>`
  margin-top: 4px;
  font-size: var(--font-size-small);
  line-height: 1.4;
  color: var(--ink-dark);
  white-space: pre-wrap;
  word-break: break-word;
  display: ${({ $clamped }) => ($clamped ? '-webkit-box' : 'block')};
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${({ $clamped, $lines }) => ($clamped ? `${$lines}` : 'unset')};
  overflow: ${({ $clamped }) => ($clamped ? 'hidden' : 'visible')};
`;

const ExpandToggle = styled.button`
  align-self: flex-start;
  margin-top: 6px;
  padding: 2px 0;
  background: none;
  border: none;
  color: var(--secondary-base);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid var(--secondary-base);
    outline-offset: 2px;
  }
`;

const MemoryMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
`;

const TypeChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--sky-light);
  color: var(--ink-dark);
  font-size: 11px;
  font-weight: 500;
  text-transform: capitalize;
`;

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: none;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: var(--flora-hover-subtle);
  }

  &:focus-visible {
    outline: 2px solid var(--secondary-base);
    outline-offset: 1px;
  }
`;
